import { useState, type FormEvent, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import type { Role } from '@fametrue/shared';
import { useAuth } from '../auth/AuthProvider';
import { useErrorMessage } from '../lib/useErrorMessage';
import {
  useAdminBrands,
  useAdminUsers,
  useCompanies,
  useCreateUser,
  useDeleteUser,
  useReissueInvite,
  useUpdateUserBrands,
  useUpdateUserStatus,
  type AdminBrand,
  type InviteResult,
} from './api';
import { BrandChecklist } from './BrandChecklist';

/**
 * Censimento utenti e inviti.
 *
 * La piattaforma non invia e-mail: il link di attivazione compare qui una sola
 * volta, e l'admin lo consegna al destinatario per un canale suo. A database
 * resta solo l'hash del token.
 *
 * I brand visibili sono per utente: un sottoinsieme di quelli della company,
 * scelto qui alla creazione e modificabile dalla tabella. Senza selezione il
 * cliente non vede alcun brand.
 *
 * La propria riga non porta comandi: il server rifiuta il cambio di stato, la
 * riemissione dell'invito e l'eliminazione su sé stessi, e un pulsante che
 * fallisce sempre è peggio di un pulsante assente.
 */
export function UsersPanel(): ReactNode {
  const { t } = useTranslation();
  const users = useAdminUsers();
  const companies = useCompanies();
  const brands = useAdminBrands();
  const createUser = useCreateUser();
  const reissue = useReissueInvite();
  const updateBrands = useUpdateUserBrands();
  const updateStatus = useUpdateUserStatus();
  const removeUser = useDeleteUser();
  const describeError = useErrorMessage();
  const { user: me } = useAuth();

  const [email, setEmail] = useState('');
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [role, setRole] = useState<Role>('client');
  const [companyId, setCompanyId] = useState('');
  const [brandIds, setBrandIds] = useState<string[]>([]);
  const [invite, setInvite] = useState<InviteResult | null>(null);
  const [copied, setCopied] = useState(false);
  const [editing, setEditing] = useState<{ userId: string; draft: string[] } | null>(null);
  // Conferma in due tempi, come per i brand: l'eliminazione è definitiva.
  const [confirmDelete, setConfirmDelete] = useState<string | null>(null);

  const brandsOf = (id: string | null): AdminBrand[] =>
    id ? (brands.data ?? []).filter((b) => b.companyId === id) : [];

  function onSubmit(event: FormEvent): void {
    event.preventDefault();
    setInvite(null);

    createUser.mutate(
      {
        email: email.trim(),
        firstName: firstName.trim(),
        lastName: lastName.trim(),
        role,
        ...(role === 'client' && companyId ? { companyId, brandIds } : {}),
      },
      {
        onSuccess: (res) => {
          setInvite(res.invite);
          setEmail('');
          setFirstName('');
          setLastName('');
          setBrandIds([]);
        },
      },
    );
  }

  const companyName = (id: string | null): string =>
    id ? (companies.data?.find((c) => c.id === id)?.name ?? '—') : '—';

  return (
    <section className="admin-card">
      <header className="admin-card-head">
        <h2>{t('admin.users.title')}</h2>
        <p>{t('admin.users.subtitle')}</p>
      </header>

      <form className="admin-grid-form" onSubmit={onSubmit}>
        <div className="admin-field">
          <label htmlFor="user-email">{t('admin.users.email')}</label>
          <input
            id="user-email"
            type="email"
            required
            value={email}
            onChange={(e) => setEmail(e.target.value)}
          />
        </div>
        <div className="admin-field">
          <label htmlFor="user-first">{t('admin.users.firstName')}</label>
          <input
            id="user-first"
            type="text"
            required
            value={firstName}
            onChange={(e) => setFirstName(e.target.value)}
          />
        </div>
        <div className="admin-field">
          <label htmlFor="user-last">{t('admin.users.lastName')}</label>
          <input
            id="user-last"
            type="text"
            required
            value={lastName}
            onChange={(e) => setLastName(e.target.value)}
          />
        </div>
        <div className="admin-field">
          <label htmlFor="user-role">{t('admin.users.role')}</label>
          <select id="user-role" value={role} onChange={(e) => setRole(e.target.value as Role)}>
            <option value="client">{t('shell.roleClient')}</option>
            <option value="admin">{t('shell.roleAdmin')}</option>
          </select>
        </div>
        {role === 'client' ? (
          <div className="admin-field">
            <label htmlFor="user-company">{t('admin.users.company')}</label>
            <select
              id="user-company"
              required
              value={companyId}
              onChange={(e) => {
                setCompanyId(e.target.value);
                // La selezione appartiene alla company: cambiandola riparte vuota.
                setBrandIds([]);
              }}
            >
              <option value="">{t('admin.users.chooseCompany')}</option>
              {(companies.data ?? []).map((c) => (
                <option key={c.id} value={c.id}>
                  {c.name}
                </option>
              ))}
            </select>
          </div>
        ) : null}
        {role === 'client' && companyId ? (
          <div className="admin-field admin-field-wide">
            <label>{t('admin.users.brands')}</label>
            <p className="admin-hint">{t('admin.users.brandsHint')}</p>
            <BrandChecklist
              brands={brandsOf(companyId)}
              selected={brandIds}
              onChange={setBrandIds}
              disabled={createUser.isPending}
            />
          </div>
        ) : null}
        <div className="admin-field admin-field-action">
          <button type="submit" className="admin-btn primary" disabled={createUser.isPending}>
            {t('admin.users.create')}
          </button>
        </div>
      </form>

      {createUser.isError ? (
        <p className="admin-alert error">{describeError(createUser.error)}</p>
      ) : null}

      {updateStatus.isError || reissue.isError || removeUser.isError ? (
        <p className="admin-alert error">
          {describeError(updateStatus.error ?? reissue.error ?? removeUser.error)}
        </p>
      ) : null}

      {invite ? (
        <div className="admin-alert success">
          <strong>{t('admin.users.inviteReady')}</strong>
          <p>{t('admin.users.inviteHint')}</p>
          <div className="admin-copy-row">
            <code className="admin-invite">{invite.url}</code>
            <button
              type="button"
              className="admin-btn"
              onClick={() => {
                void navigator.clipboard.writeText(invite.url).then(() => setCopied(true));
              }}
            >
              {copied ? t('admin.users.copied') : t('admin.users.copy')}
            </button>
          </div>
        </div>
      ) : null}

      <table className="admin-table">
        <thead>
          <tr>
            <th>{t('admin.users.user')}</th>
            <th>{t('admin.users.role')}</th>
            <th>{t('admin.users.company')}</th>
            <th>{t('admin.users.brands')}</th>
            <th>{t('admin.users.status')}</th>
            <th />
          </tr>
        </thead>
        <tbody>
          {(users.data ?? []).map((user) => {
            const isEditing = editing?.userId === user.id;
            const companyBrands = brandsOf(user.companyId);
            const isSelf = user.id === me?.id;
            const disabled = user.status === 'disabled';
            return [
              <tr key={user.id}>
                <td>
                  <div className="admin-strong">
                    {user.firstName} {user.lastName}
                  </div>
                  <code className="admin-mono">{user.email}</code>
                </td>
                <td>{user.role === 'admin' ? t('shell.roleAdmin') : t('shell.roleClient')}</td>
                <td>{companyName(user.companyId)}</td>
                <td>
                  {user.role === 'client'
                    ? t('admin.users.brandCount', {
                        selected: user.brandIds.length,
                        total: companyBrands.length,
                      })
                    : '—'}
                </td>
                <td>
                  <span className={`admin-status ${user.status}`}>
                    {t(`admin.users.status_${user.status}`)}
                  </span>
                </td>
                <td className="admin-right">
                  {user.role === 'client' ? (
                    <button
                      type="button"
                      className="admin-btn"
                      disabled={isEditing}
                      onClick={() => {
                        updateBrands.reset();
                        setEditing({ userId: user.id, draft: user.brandIds });
                      }}
                    >
                      {t('admin.users.editBrands')}
                    </button>
                  ) : null}
                  {isSelf ? null : (
                    <>
                      <button
                        type="button"
                        className="admin-btn"
                        disabled={updateStatus.isPending}
                        onClick={() => {
                          reissue.reset();
                          removeUser.reset();
                          updateStatus.mutate({
                            userId: user.id,
                            status: disabled ? 'active' : 'disabled',
                          });
                        }}
                      >
                        {t(disabled ? 'admin.users.enable' : 'admin.users.disable')}
                      </button>
                      <button
                        type="button"
                        className="admin-btn"
                        disabled={reissue.isPending}
                        onClick={() => {
                          updateStatus.reset();
                          removeUser.reset();
                          reissue.mutate(user.id, { onSuccess: (r) => setInvite(r.invite) });
                        }}
                      >
                        {t('admin.users.reinvite')}
                      </button>
                      <button
                        type="button"
                        className="admin-btn"
                        disabled={confirmDelete === user.id}
                        onClick={() => {
                          updateStatus.reset();
                          reissue.reset();
                          removeUser.reset();
                          setConfirmDelete(user.id);
                        }}
                      >
                        {t('admin.users.delete')}
                      </button>
                    </>
                  )}
                </td>
              </tr>,
              isEditing && editing ? (
                <tr key={`${user.id}-brands`} className="admin-row-editor">
                  <td colSpan={6}>
                    <BrandChecklist
                      brands={companyBrands}
                      selected={editing.draft}
                      onChange={(draft) => setEditing({ userId: user.id, draft })}
                      disabled={updateBrands.isPending}
                    />
                    {updateBrands.isError ? (
                      <p className="admin-alert error">{describeError(updateBrands.error)}</p>
                    ) : null}
                    <div className="admin-actions">
                      <button
                        type="button"
                        className="admin-btn primary"
                        disabled={updateBrands.isPending}
                        onClick={() =>
                          updateBrands.mutate(
                            { userId: user.id, brandIds: editing.draft },
                            { onSuccess: () => setEditing(null) },
                          )
                        }
                      >
                        {t('admin.users.saveBrands')}
                      </button>
                      <button
                        type="button"
                        className="admin-btn"
                        disabled={updateBrands.isPending}
                        onClick={() => setEditing(null)}
                      >
                        {t('admin.users.cancel')}
                      </button>
                    </div>
                  </td>
                </tr>
              ) : null,
              confirmDelete === user.id ? (
                <tr key={`${user.id}-delete`} className="admin-row-editor">
                  <td colSpan={6}>
                    <p className="admin-hint">{t('admin.users.deleteHint')}</p>
                    <div className="admin-actions">
                      <button
                        type="button"
                        className="admin-btn danger"
                        disabled={removeUser.isPending}
                        onClick={() =>
                          removeUser.mutate(user.id, { onSettled: () => setConfirmDelete(null) })
                        }
                      >
                        {t('admin.users.confirmDelete')}
                      </button>
                      <button
                        type="button"
                        className="admin-btn"
                        disabled={removeUser.isPending}
                        onClick={() => setConfirmDelete(null)}
                      >
                        {t('admin.users.cancel')}
                      </button>
                    </div>
                  </td>
                </tr>
              ) : null,
            ];
          })}
        </tbody>
      </table>
    </section>
  );
}
